/**
 * Returns an array of script tags to be rendered in the head for CookieHub
 * consent banner configuration.
 *
 * Integrates with PostHog analytics for consent-aware tracking. CookieHub
 * fires onInitialise when its persisted consent state is loaded, then
 * onAllow/onRevoke as the user changes categories. We dispatch a custom
 * `cookiehub_consent_changed` event that analytics.js listens to in order
 * to opt PostHog in or out of capturing.
 *
 * @see https://docs.cookiehub.com/advanced/javascript-api
 *
 * @returns {Array<Object>} Array of script tags to be rendered in the head
 */
export const CookieHubConfig = () => {
  if (import.meta.env.VITE_COOKIEHUB_ENABLED !== 'true') {
    return []
  }

  // CookieHub provides a separate "dev" script URL that bypasses the
  // production cache and reflects unpublished changes from the CookieHub
  // admin. Use it locally when iterating on banner config; the dev widget
  // is active for up to 7 days after the last admin update. Production
  // always uses the published cdn URL.
  const useDevScript = import.meta.env.VITE_COOKIEHUB_USE_DEV_SCRIPT === 'true'
  const scriptId = 'f078676d'
  const scriptSrc = useDevScript
    ? `https://dash.cookiehub.com/dev/${scriptId}.js`
    : `https://cdn.cookiehub.eu/c2/${scriptId}.js`

  return [
    {
      src: scriptSrc,
    },
    {
      type: 'text/javascript',
      innerHTML: `
(function() {
  function dispatchAnalyticsConsent() {
    var hasAnalytics = window.cookiehub && window.cookiehub.hasConsented
      ? window.cookiehub.hasConsented('analytics')
      : false;
    window.dispatchEvent(new CustomEvent('cookiehub_consent_changed', {
      detail: { categories: { analytics: hasAnalytics } }
    }));
  }

  function loadCookieHub() {
    if (typeof window.cookiehub === 'undefined') {
      // CookieHub script hasn't finished loading yet — retry shortly.
      setTimeout(loadCookieHub, 100);
      return;
    }

    var cpm = {
      onInitialise: function() {
        dispatchAnalyticsConsent();
      },
      onAllow: function(category) {
        if (category === 'analytics') dispatchAnalyticsConsent();
      },
      onRevoke: function(category) {
        if (category === 'analytics') dispatchAnalyticsConsent();
      },
    };
    window.cookiehub.load(cpm);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadCookieHub);
  } else {
    loadCookieHub();
  }
})();
`,
    },
  ]
}
